// apps/patient/src/pages/Profile.tsx

import { useState } from 'react';
import { usePatient } from '../contexts/PatientContext';
import { patientApiService } from '../services/api';
import { PageHeader } from '../components/layout/PageHeader';
import { PatientProfileView } from '../components/profile/PatientProfileView';
import { PatientProfileEdit } from '../components/profile/PatientProfileEdit';
import { SubscriptionSection } from '../components/profile/SubscriptionSection';
import { useFeatures } from '../contexts/FeatureContext';

export function Profile() {
  const { patient: user, refreshPatient } = usePatient();
  const { isExercisePlatform, accessType } = useFeatures();
  // Standalone Ready2Exercise users manage their $20/month subscription here; org-linked users never see it
  const showSubscription = isExercisePlatform && accessType === 'standalone';
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (formData: any) => {
    try {
      setIsSaving(true);
      setError(null);
      
      // Update user fields (contact info + security only)
      const userUpdatePayload = {
        phone: formData.phone,
        biometric_mfa: formData.biometric_mfa
      };

      // Update user profile
      await patientApiService.updateMyProfile(userUpdatePayload);

      // Update patient record (notification preferences + demographics)
      if (user?.patient?.id) {
        const patientUpdatePayload = {
          ...user.patient,
          phone: formData.phone,
          address: formData.address,
          emergency_contacts: formData.emergency_contacts,
          email_preferences: formData.email_preferences,
          sms_preferences: formData.sms_preferences
        };

        await patientApiService.updatePatientRecord(user.patient.id, patientUpdatePayload);
      }

      // Refresh the patient context to get updated data
      await refreshPatient();
      
      setIsEditing(false);
      setIsSaving(false); // Make sure to reset saving state on success
    } catch (err: any) {
      console.error('Failed to save profile:', err);
      setError(err.message || 'Failed to update profile. Please try again.');
      setIsSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 lg:p-8">
      <PageHeader
        title="My Profile"
        actions={
          !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Edit Profile
            </button>
          )
        }
      />

      {error && (
        <div className="mt-6 mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {showSubscription && !isEditing && (
        <div className="mt-8 bg-white rounded-lg shadow-sm">
          <SubscriptionSection user={user} returnPath="/profile" />
        </div>
      )}

      <div className="mt-8 bg-white rounded-lg shadow-sm">
        {isEditing ? (
          <PatientProfileEdit
            user={user}
            onSave={handleSave}
            onCancel={() => {
              setIsEditing(false);
              setError(null);
            }}
            isSaving={isSaving}
          />
        ) : (
          <PatientProfileView user={user} />
        )}
      </div>
    </div>
  );
}